import { useCallback, useEffect, useRef, useState } from 'react';

export const useScrollbarWidth = () => {
  const [scrollBarWidth, setScrollBarWidth] = useState(0);
  const [containerNode, setContainerNode] = useState<HTMLElement | null>(null);
  const scrollbarWidthCache = useRef<number | null>(null);

  const measureScrollbar = useCallback((node: HTMLElement) => {
    // Check if scrollbar is present
    const hasScroll = node.scrollHeight > node.clientHeight;

    if (!hasScroll) {
      setScrollBarWidth(0);
      return;
    }

    // Cache scrollbar width measurement (browser scrollbar width is constant)
    if (scrollbarWidthCache.current === null) {
      const scrollDiv = document.createElement('div');
      scrollDiv.setAttribute('style', 'width: 100px; height: 100px; overflow: scroll; position:absolute; top:-9999px;');
      document.body.appendChild(scrollDiv);
      scrollbarWidthCache.current = scrollDiv.offsetWidth - scrollDiv.clientWidth;
      document.body.removeChild(scrollDiv);
    }

    setScrollBarWidth(scrollbarWidthCache.current);
  }, []);

  const handleScrollbarWidth = useCallback((node: HTMLElement | null): void => {
    setContainerNode(node);
  }, []);

  // Set up ResizeObserver to auto-detect size changes
  useEffect(() => {
    if (!containerNode) return;

    // Initial measurement
    measureScrollbar(containerNode);

    // Auto-detect size changes with ResizeObserver
    const resizeObserver = new ResizeObserver(() => {
      measureScrollbar(containerNode);
    });

    resizeObserver.observe(containerNode);

    // Cleanup on unmount
    return () => resizeObserver.disconnect();
  }, [containerNode, measureScrollbar]);

  return {
    scrollBarWidth,
    handleScrollbarWidth,
  };
};
